// app-ui/src/components/project/CreateProjectForm.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import apiClient, { TokenManager } from '../../../../shared/lib/apiClient';
import { ProjectCreatePayload, Project } from '../types';
import LlmService, { LlmResponse } from '../../channels/chat/api/llmApi';
import TextInputWithAI from '../../../../shared/ui/TextInputWithAI';
import { ImageAttachment } from '../../channels/chat/components/shared/attachements/conversationalTypes';

/** Short starter lines for the empty dashboard composer — set as the initial prompt on click. */
const MINIMAL_EXAMPLE_PROMPTS = [
    'An AI tool that filters and auto-categorizes email',
    'A calm habit tracker with weekly reviews, not guilt',
    'A marketplace connecting indie game devs with playtesters',
] as const;

/** Optional flags when a project is first created (e.g. open discovery chat panel). */
export type ProjectCreatedOptions = {
    openDiscoveryChat?: boolean;
};

interface CreateProjectFormProps {
    onProjectCreated?: (
        projectId?: number | string,
        projectName?: string,
        options?: ProjectCreatedOptions
    ) => void;
    shouldExpand?: boolean;
    onExpandChange?: (expanded: boolean) => void;
    /** Toggles the right-hand chat panel on the empty dashboard (Product Discovery opens it before POST). */
    onChatModeChange?: (isChatMode: boolean) => void;
    onBackFromChat?: () => void;
    minimalComposer?: boolean;
}

const CreateProjectForm: React.FC<CreateProjectFormProps> = ({
    onProjectCreated,
    shouldExpand = false,
    onExpandChange,
    onChatModeChange,
    onBackFromChat: _onBackFromChat,
    minimalComposer = false,
}) => {
    const [name, setName] = useState('');
    const [prompt, setPrompt] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [isLlmProcessing, setIsLlmProcessing] = useState<boolean>(false);
    const [progressUpdate, setProgressUpdate] = useState<string | null>(null);
    const [isExpanded, setIsExpanded] = useState<boolean>(shouldExpand);
    const [attachments, setAttachments] = useState<ImageAttachment[]>([]);
    const [isEditingProjectName, setIsEditingProjectName] = useState<boolean>(false);
    const projectNameInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (shouldExpand !== undefined && shouldExpand !== isExpanded) {
            setIsExpanded(shouldExpand);
        }
    }, [shouldExpand, isExpanded]);

    const handleExpandedChange = (expanded: boolean) => {
        setIsExpanded(expanded);
        onExpandChange?.(expanded);

        if (!expanded) {
            setError(null);
            setProgressUpdate(null);
            setName('');
            setPrompt('');
            setAttachments([]);
            setIsEditingProjectName(false);
            onChatModeChange?.(false);
        }
    };

    useEffect(() => {
        if (isEditingProjectName) {
            projectNameInputRef.current?.focus();
            projectNameInputRef.current?.select();
        }
    }, [isEditingProjectName]);

    /** Product Discovery & primary CTA both require a non-empty project name and description (initial prompt). */
    const hasProjectNameAndDescription = useMemo(
        () => name.trim() !== '' && prompt.trim() !== '',
        [name, prompt]
    );

    const isFormComplete = hasProjectNameAndDescription;

    const effectiveProjectName = name.trim();
    const displayProjectName = effectiveProjectName || 'Untitled Project';

    const handleRefineStart = () => {
        setIsLlmProcessing(true);
        setError(null);
    };

    const handleRefineComplete = (_response: LlmResponse) => {
        setIsLlmProcessing(false);
    };

    const handleRefineError = (err: Error) => {
        setIsLlmProcessing(false);
        setError(err.message || 'Failed to refine text with AI. Please try again.');
    };

    /**
     * Creates the project, selects it in the sidebar, and opens discovery chat on the project view.
     * Side chat opens first on the empty dashboard so the panel animates before navigation.
     */
    const startProductDiscovery = async () => {
        if (!hasProjectNameAndDescription) {
            setError('Add a project name and description to continue.');
            return;
        }

        const userId = TokenManager.getUserId();
        if (!userId) {
            setError('User ID not found. Please sign in again.');
            return;
        }

        onChatModeChange?.(true);
        setIsSubmitting(true);
        setError(null);

        try {
            const project = await apiClient<Project>('/projects', {
                method: 'POST',
                body: {
                    name: name.trim(),
                    initial_prompt: prompt.trim(),
                    user_id: userId,
                } as ProjectCreatePayload,
            });
            onProjectCreated?.(project.id, project.name, { openDiscoveryChat: true });
        } catch (err: unknown) {
            onChatModeChange?.(false);
            const message = err instanceof Error ? err.message : 'Failed to create project.';
            setError(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (hasProjectNameAndDescription) {
            void startProductDiscovery();
        }
    };

    const handleBuildClick = async (componentId: string, text: string, existingProjectId?: number) => {
        if (!componentId) {
            console.error('Component ID is required');
            setError('System error: Component ID is missing.');
            return;
        }

        if (!effectiveProjectName) {
            setError('Project name is required.');
            return;
        }

        if (!text.trim()) {
            setError('Initial prompt is required.');
            return;
        }

        const userId = TokenManager.getUserId();
        if (!userId) {
            setError('User ID not found. Please sign in again.');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            let project: Project;
            if (existingProjectId != null) {
                project = {
                    id: existingProjectId,
                    name: effectiveProjectName,
                    initial_prompt: text.trim(),
                    created_at: '',
                    updated_at: null,
                };
            } else {
                project = await apiClient<Project>('/projects', {
                    method: 'POST',
                    body: {
                        name: effectiveProjectName,
                        initial_prompt: text.trim(),
                        user_id: userId,
                    } as ProjectCreatePayload,
                });
            }

            try {
                const projectIdStr = String(project.id).trim();
                if (!projectIdStr) {
                    throw new Error('Project ID is invalid.');
                }
                console.log(`Processing with ${componentId}... for project ID: ${project.id}`);
                await LlmService.processWithLlm(
                    componentId,
                    text,
                    projectIdStr,
                    true,
                    (update: string) => {
                        setProgressUpdate(update);
                    }
                );
                setProgressUpdate('User flow saved successfully!');
                setTimeout(() => {
                    setName('');
                    setPrompt('');
                    setProgressUpdate(null);
                    setIsExpanded(false);
                    onChatModeChange?.(false);
                    onProjectCreated?.(project.id, effectiveProjectName);
                }, 2000);
            } catch (llmErr: unknown) {
                console.error('Error generating user flow:', llmErr);
                setError('Failed to generate user flow. Please try again from the project page.');
                setTimeout(() => {
                    setName('');
                    setPrompt('');
                    setProgressUpdate(null);
                    onProjectCreated?.(project.id, effectiveProjectName);
                }, 3000);
            }
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to create project.';
            setError(message);
            setProgressUpdate(null);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={`w-full ${isExpanded ? 'h-full flex flex-col' : ''}`}>
            {!isExpanded ? (
                <div
                    onClick={() => handleExpandedChange(true)}
                    className="flex items-center justify-center py-4 px-6 bg-white/10 border border-white/20 rounded-xl cursor-pointer hover:bg-white/15 transition-all duration-300 backdrop-blur-sm group"
                >
                    <div className="flex items-center space-x-3 text-white/70 group-hover:text-white">
                        <svg
                            className="w-5 h-5 text-purple-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        <span className="text-sm font-medium">Create new Project</span>
                    </div>
                </div>
            ) : (
                <div className="h-full flex flex-col">
                    <form onSubmit={handleFormSubmit} className="flex-1 flex flex-col min-h-0">
                        {minimalComposer ? (
                            <div
                                className="
                                    relative flex flex-1 flex-col min-h-[105%] overflow-hidden rounded-2xl
                                    border border-white/[0.07]
                                    bg-gradient-to-b from-white/[0.075] via-white/[0.05] to-white/[0.03]
                                    shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06),0_18px_56px_-20px_rgba(0,0,0,0.5)]
                                    backdrop-blur-xl
                                "
                            >
                                <div
                                    aria-hidden
                                    className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_0%,rgba(139,92,246,0.16)_0%,rgba(139,92,246,0.04)_45%,transparent_75%)]"
                                />
                                <div
                                    aria-hidden
                                    className="pointer-events-none absolute inset-0 opacity-[0.045] mix-blend-screen [background-image:radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:3px_3px]"
                                />

                                <div className="relative flex-1 space-y-4 overflow-y-auto pr-1 px-6 pt-6 pb-2 md:px-8 md:pt-7 md:pb-2">
                                    <div className="flex-shrink-0">
                                        {isEditingProjectName ? (
                                            <input
                                                ref={projectNameInputRef}
                                                type="text"
                                                id="projectNameMinimal"
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                onBlur={() => setIsEditingProjectName(false)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === 'Escape') {
                                                        e.preventDefault();
                                                        setIsEditingProjectName(false);
                                                    }
                                                }}
                                                className="w-full rounded-xl bg-transparent px-4 py-2.5 text-sm text-white placeholder-gray-400 ring-1 ring-white/8 backdrop-blur-sm transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-violet-400/60"
                                                placeholder="Project name"
                                                disabled={isSubmitting || isLlmProcessing}
                                                aria-label="Project name"
                                            />
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <h3
                                                    className="cursor-text text-lg font-semibold tracking-tight text-white md:text-xl"
                                                    title="Double-click to edit"
                                                    onDoubleClick={() => {
                                                        if (!isSubmitting && !isLlmProcessing) {
                                                            setIsEditingProjectName(true);
                                                        }
                                                    }}
                                                >
                                                    {displayProjectName}
                                                </h3>
                                                <button
                                                    type="button"
                                                    onClick={() => setIsEditingProjectName(true)}
                                                    className="shrink-0 rounded-md p-1.5 text-gray-400 transition-colors hover:bg-white/5 hover:text-gray-200"
                                                    title="Edit project name"
                                                    aria-label="Edit project name"
                                                    disabled={isSubmitting || isLlmProcessing}
                                                >
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5M18.586 3.586a2 2 0 112.828 2.828L12 15.828 8 16l.172-4 10.414-10.414z"
                                                        />
                                                    </svg>
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <div className="mb-0 flex-shrink-0 space-y-3">
                                        <TextInputWithAI
                                            value={prompt}
                                            onChange={setPrompt}
                                            componentId="gen_initialPrompt"
                                            type="textarea"
                                            id="initialPrompt"
                                            label={undefined}
                                            placeholder="What are you building? Example: an AI assistant that triages support tickets."
                                            required
                                            disabled={isSubmitting}
                                            rows={4}
                                            autoResize={false}
                                            maxHeight="176px"
                                            minHeight="96px"
                                            inputClassName="overflow-y-auto !border-0 !bg-white/[0.035] !ring-1 !ring-white/[0.08] !rounded-xl focus:!ring-2 focus:!ring-violet-400/55 focus:!border-transparent"
                                            onRefineStart={handleRefineStart}
                                            onRefineComplete={handleRefineComplete}
                                            onRefineError={handleRefineError}
                                            showSuccessMessage={true}
                                            enableImageUpload={true}
                                            onAttachmentsChange={setAttachments}
                                        />
                                        {!prompt.trim() && (
                                            <div
                                                className="flex flex-wrap gap-2"
                                                role="group"
                                                aria-label="Example prompts"
                                            >
                                                {MINIMAL_EXAMPLE_PROMPTS.map((example) => (
                                                    <button
                                                        key={example}
                                                        type="button"
                                                        onClick={() => setPrompt(example)}
                                                        disabled={isSubmitting || isLlmProcessing}
                                                        className="max-w-full rounded-lg bg-white/[0.045] px-3 py-2 text-left text-xs leading-snug text-gray-300 ring-1 ring-transparent transition-colors hover:bg-white/[0.075] hover:text-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
                                                    >
                                                        {example}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex-shrink-0 space-y-2">
                                        {error && (
                                            <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-4 backdrop-blur-sm">
                                                <p className="flex items-center text-sm text-red-300">
                                                    <svg
                                                        className="mr-2 h-4 w-4"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                        />
                                                    </svg>
                                                    {error}
                                                </p>
                                            </div>
                                        )}

                                        {progressUpdate && (
                                            <div className="rounded-xl border border-purple-400/30 bg-purple-500/10 p-4 backdrop-blur-sm">
                                                <p className="flex items-center text-sm text-purple-300">
                                                    <svg
                                                        className="mr-2 h-4 w-4 animate-spin"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <circle
                                                            className="opacity-25"
                                                            cx="12"
                                                            cy="12"
                                                            r="10"
                                                            stroke="currentColor"
                                                            strokeWidth="4"
                                                        ></circle>
                                                        <path
                                                            className="opacity-75"
                                                            fill="currentColor"
                                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                        ></path>
                                                    </svg>
                                                    {progressUpdate}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="relative flex-shrink-0 px-6 pb-6 pt-1 md:px-8 md:pb-8">
                                    <button
                                        id="btn_build_with_agents"
                                        type="button"
                                        onClick={() => void startProductDiscovery()}
                                        disabled={isSubmitting || isLlmProcessing || !isFormComplete}
                                        className={`
                                            touch-target inline-flex w-full items-center justify-center rounded-xl px-6 py-3.5
                                            text-sm font-semibold tracking-tight
                                            transition-all duration-300
                                            focus:outline-none focus:ring-2 focus:ring-violet-400/80 focus:ring-offset-2 focus:ring-offset-[#0A071B]
                                            ${
                                                isSubmitting || isLlmProcessing || !isFormComplete
                                                    ? 'cursor-not-allowed border border-white/15 bg-gradient-to-r from-purple-900/80 to-purple-800/70 text-white/70 opacity-60'
                                                    : `animate-cta-breathe border border-white/25 bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600
                                                       text-white shadow-[0_8px_32px_-6px_rgba(124,58,237,0.55)]
                                                       hover:scale-[1.01] hover:from-violet-500 hover:via-purple-500 hover:to-fuchsia-500
                                                       active:scale-[0.99] active:brightness-95`
                                            }
                                        `}
                                    >
                                        <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M13 10V3L4 14h7v7l9-11h-7z"
                                            />
                                        </svg>
                                        Build with agents
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="flex-1 space-y-4 overflow-y-auto pr-1">
                                    <div className="flex-shrink-0 space-y-2">
                                        <label htmlFor="projectName" className="block text-sm font-medium text-white">
                                            Project Name <span className="text-red-400">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="projectName"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            required
                                            className="w-full rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-white placeholder-gray-400 backdrop-blur-sm transition-all duration-300 focus:border-purple-400/50 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                                            placeholder="Enter project name"
                                            disabled={isSubmitting || isLlmProcessing}
                                        />
                                    </div>

                                    <div className="mb-0 flex-shrink-0 space-y-2">
                                        <TextInputWithAI
                                            value={prompt}
                                            onChange={setPrompt}
                                            componentId="gen_initialPrompt"
                                            type="textarea"
                                            id="initialPrompt"
                                            label="Description"
                                            placeholder={
                                                'Describe the product you want to build...\n\ne.g. "An AI tool that filters emails and auto-categorizes them"'
                                            }
                                            required
                                            disabled={isSubmitting}
                                            rows={60}
                                            autoResize={false}
                                            maxHeight="520px"
                                            minHeight="360px"
                                            inputClassName="overflow-y-auto"
                                            onRefineStart={handleRefineStart}
                                            onRefineComplete={handleRefineComplete}
                                            onRefineError={handleRefineError}
                                            showSuccessMessage={true}
                                            enableImageUpload={true}
                                            onAttachmentsChange={setAttachments}
                                        />
                                    </div>

                                    <div className="flex-shrink-0 space-y-2">
                                        {error && (
                                            <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-4 backdrop-blur-sm">
                                                <p className="flex items-center text-sm text-red-300">
                                                    <svg
                                                        className="mr-2 h-4 w-4"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                        />
                                                    </svg>
                                                    {error}
                                                </p>
                                            </div>
                                        )}

                                        {progressUpdate && (
                                            <div className="rounded-xl border border-purple-400/30 bg-purple-500/10 p-4 backdrop-blur-sm">
                                                <p className="flex items-center text-sm text-purple-300">
                                                    <svg
                                                        className="mr-2 h-4 w-4 animate-spin"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <circle
                                                            className="opacity-25"
                                                            cx="12"
                                                            cy="12"
                                                            r="10"
                                                            stroke="currentColor"
                                                            strokeWidth="4"
                                                        ></circle>
                                                        <path
                                                            className="opacity-75"
                                                            fill="currentColor"
                                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                        ></path>
                                                    </svg>
                                                    {progressUpdate}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="flex-shrink-0 space-y-2 pt-2">
                                    <button
                                        type="button"
                                        onClick={() => void startProductDiscovery()}
                                        disabled={!hasProjectNameAndDescription || isSubmitting || isLlmProcessing}
                                        className="flex w-full items-center justify-center gap-2 py-2 text-sm text-gray-300 transition-colors hover:text-purple-300 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <svg
                                            className="h-4 w-4 flex-shrink-0"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                                            />
                                        </svg>
                                        <span>Product Discovery – Align on users, pain and MVP</span>
                                    </button>

                                    <button
                                        id="gen_userflow"
                                        type="button"
                                        onClick={() => handleBuildClick('gen_userflow', prompt)}
                                        disabled={isSubmitting || isLlmProcessing || !isFormComplete}
                                        className={`
                                            touch-target inline-flex w-full items-center justify-center rounded-xl px-6 py-3
                                            text-sm font-semibold
                                            transition-all duration-300
                                            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#0A071B]
                                            ${
                                                isSubmitting || isLlmProcessing || !isFormComplete
                                                    ? 'cursor-not-allowed border border-white/20 bg-gradient-to-r from-purple-600 to-purple-500 text-white opacity-50'
                                                    : 'transform bg-gradient-to-r from-purple-600 to-purple-500 text-white ring-2 ring-white/30 hover:scale-[1.02] hover:from-purple-700 hover:to-purple-600 hover:shadow-2xl'
                                            }
                                        `}
                                    >
                                        <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M13 10V3L4 14h7v7l9-11h-7z"
                                            />
                                        </svg>
                                        Let&apos;s build it
                                    </button>
                                </div>
                            </>
                        )}
                    </form>
                </div>
            )}
        </div>
    );
};

export default CreateProjectForm;
